import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';

import type { FactItem, RationaleResult } from './useRationale';

/**
 * Batch endpoint for AI-generated recommendation rationales (SCRUM-256).
 * The customer drawer sends all recommended products in ONE request instead of
 * N per-card calls. nginx proxies relative /api/* URLs to bankiq-api.
 */
const RATIONALE_BATCH_URL = '/api/intelligence/recommendations/rationale/batch';

/** Demo tenant ID shared across all frontend API calls. */
// SCRUM-643: tenant from the App ID token claim (SCRUM-102), no hard-coded header.

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RationaleBatchItem {
  /** Response dictionary key — use the product id so the caller can look results up. */
  id: string;
  productName: string;
  /** Number of ICP (Ideal Customer Profile) criteria the customer matched. */
  icpMatchCount: number;
  /** Supporting context facts; non-PII only. */
  facts: FactItem[];
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchRationales(
  items: RationaleBatchItem[],
): Promise<Record<string, RationaleResult>> {
  const res = await apiFetch(RATIONALE_BATCH_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rationales: items }),
  });

  if (!res.ok) {
    throw new Error(
      `POST ${RATIONALE_BATCH_URL} failed: ${res.status.toString()} ${res.statusText}`,
    );
  }

  return res.json() as Promise<Record<string, RationaleResult>>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fetches AI rationales for all recommended products in ONE HTTP request
 * (SCRUM-256 batch endpoint, SCRUM-266 frontend wiring). Returns a dictionary
 * keyed by the id passed in each {@link RationaleBatchItem} (the product id).
 *
 * Query posture (shared with the retired dashboard's useInsightSummaries, SCRUM-323):
 * - `enabled: false` when items is empty — skips the query entirely.
 * - `staleTime: 5 min` — prevents re-fetch on re-render within a session.
 * - `throwOnError: false` — rationale is a non-fatal enhancement; cards fall back
 *   silently (no text/badge) for any item whose result is missing or
 *   `aiGenerated: false`.
 *
 * Unlike the single-call {@link import('./useRationale')} hook, this does NOT
 * throw-on-`aiGenerated:false` or retry. Cold-start blanking is no longer the
 * live failure mode after the watsonx plan upgrade (SCRUM-260), server-side
 * cache (SCRUM-253), deterministic decoding (SCRUM-257), and min-scale=1
 * (SCRUM-255). Per-item unavailability is handled gracefully in the UI.
 */
export function useRationales(items: RationaleBatchItem[]) {
  // Sort ids for a stable cache key regardless of item order.
  const idsKey = items
    .map((i) => i.id)
    .sort()
    .join(',');

  return useQuery<Record<string, RationaleResult>>({
    queryKey: ['rationales-batch', idsKey],
    queryFn: () => fetchRationales(items),
    enabled: items.length > 0,
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
  });
}
